import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, ArrowLeft, KeyRound, Chrome, Lock } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const errText = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export default function Login() {
  const { setUser, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("email");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState("otp");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    navigate("/app", { replace: true });
    return null;
  }

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/otp/request", { email });
      toast.success("Login code sent to your email");
      setStep("otp");
    } catch (err) {
      toast.error(errText(err, "Could not send code"));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/verify", { email, code });
      setUser(r.data);
      toast.success(`Welcome, ${r.data.name}`);
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(errText(err, "Invalid code"));
    } finally {
      setBusy(false);
    }
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      toast.success(`Welcome back, ${r.data.name}`);
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(errText(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F6F5] relative overflow-hidden flex items-center justify-center px-4" data-testid="login-page">
      <div className="hero-grid-bg absolute inset-0" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-emerald-500/10 blur-[120px] rounded-full" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md rounded-3xl backdrop-blur-xl bg-white/90 border border-slate-200 shadow-2xl p-8"
      >
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-600 transition-colors duration-200 mb-6" data-testid="login-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <span className="bg-white rounded-xl px-3 py-1.5 inline-flex">
            <img src="/logo.png" alt="GREENRGY" className="h-8" data-testid="login-logo" />
          </span>
          <span className="font-display font-extrabold text-lg tracking-tight text-slate-900">GREENRGY <span className="text-emerald-600">· ACCOUNTS</span></span>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Portal Access</h1>
        <p className="text-sm text-slate-600 mt-1 mb-7">Sign in to the accounting portal — Admin, Accounts or Backoffice.</p>

        <Button
          onClick={googleLogin}
          data-testid="google-login-btn"
          className="w-full bg-white text-slate-900 hover:bg-emerald-400 hover:text-slate-950 font-semibold h-11 transition-colors duration-200"
        >
          <Chrome className="w-4 h-4 mr-2" /> Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-slate-100" />
          <span className="text-[11px] font-mono2 uppercase tracking-[0.2em] text-slate-500">or with email</span>
          <div className="h-px flex-1 bg-slate-100" />
        </div>

        <div className="flex rounded-full border border-slate-300 overflow-hidden mb-5" data-testid="login-mode-toggle">
          {[["otp", "Email OTP"], ["password", "Password"]].map(([m, l]) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`login-mode-${m}`}
              className={`flex-1 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${mode === m ? "bg-emerald-500 text-slate-950" : "text-slate-600 hover:text-slate-800"}`}>
              {l}
            </button>
          ))}
        </div>

        {mode === "password" ? (
          <form onSubmit={passwordLogin} className="space-y-4" data-testid="password-login-form">
            <div className="space-y-1.5">
              <label className="text-xs text-slate-600">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@greeergy.in" data-testid="password-email-input"
                  className="pl-9 bg-white border-slate-300 text-slate-900 h-11"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-slate-600">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" data-testid="password-input"
                  className="pl-9 bg-white border-slate-300 text-slate-900 h-11"
                />
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="password-login-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-11 transition-colors duration-200">
              <KeyRound className="w-4 h-4 mr-2" /> {busy ? "Signing in…" : "Sign In"}
            </Button>
            <p className="text-center text-xs text-slate-500">No password yet? Sign in with OTP or Google, then set one under My Profile.</p>
          </form>
        ) : step === "email" ? (
          <form onSubmit={requestOtp} className="space-y-4" data-testid="otp-email-form">
            <div className="space-y-1.5">
              <label className="text-xs text-slate-600">Work email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@greeergy.in" data-testid="otp-email-input"
                  className="pl-9 bg-white border-slate-300 text-slate-900 h-11"
                />
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="otp-send-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-11 transition-colors duration-200">
              {busy ? "Sending…" : "Send Login Code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="space-y-5" data-testid="otp-verify-form">
            <p className="text-sm text-slate-600">Enter the 6-digit code sent to <span className="text-emerald-600">{email}</span></p>
            <div className="flex justify-center" data-testid="otp-code-input">
              <InputOTP maxLength={6} value={code} onChange={setCode}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="bg-white border-slate-300 text-slate-900" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button type="submit" disabled={busy || code.length !== 6} data-testid="otp-verify-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-11 transition-colors duration-200">
              <KeyRound className="w-4 h-4 mr-2" /> {busy ? "Verifying…" : "Verify & Sign In"}
            </Button>
            <button type="button" onClick={() => setStep("email")} data-testid="otp-change-email-btn"
              className="w-full text-center text-xs text-slate-500 hover:text-emerald-600 transition-colors duration-200">
              Use a different email
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
